// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import moment from 'moment'
import React, { useState } from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { removeTypename } from '../../../common/utils'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import withUser from '../../auth/withUser'
import { i18n } from '../../index'
import ImageOverlay from '../ImageOverlay/ImageOverlay'
import MonthlyInfoPage from '../MonthlyInfoPage'
import RequiresAction from '../RequiresAction'
import Images from '../components/Images'
import Links from '../components/Links'
import { fetchKitchenPageScoreCardStats, fetchMonthlyInfo, fetchMonthlyInfoScoreCard } from '../monthlyInfoQueries'
import type { MonthlyInfoKitchen, MonthlyInfoVariables } from '../monthlyInfoTypes'
import css from './kitchenPage.css'

type ProductGroupWastageRowProps = {
  stationId: string
  date: string
  data: MonthlyInfoKitchen
  canEdit: boolean
  wastageBoundaries:
    | {
        lowerWastageBounds: number
        upperWastageBounds: number
      }
    | null
    | undefined
  refetch: (...args: Array<any>) => any
}

const ProductGroupWastageRow = ({
  stationId,
  date,
  data,
  canEdit,
  wastageBoundaries,
  refetch,
}: ProductGroupWastageRowProps) => {
  const [expanded, setExpanded] = useState(false)

  const toggleExpanded = () => {
    setExpanded(!expanded)
  }

  const level3ProductGroup = data.filter((item) => item.productGroupId.length === 7)[0]
  const level4ProductGroups = data
    .filter((item) => item.productGroupId.length > 7)
    .sort(
      (a, b) =>
        i18n.t(`monthlyInfo.heading.productGroup${a.productGroupName}`) >
        i18n.t(`monthlyInfo.heading.productGroup${b.productGroupName}`),
    )

  const { id, productGroupName, totalWastageAmount, totalWastageVolume, totalSalesAmount, totalSalesVolume } =
    level3ProductGroup
  const percentage = (level3ProductGroup.totalWastageAmount / level3ProductGroup.totalSalesAmount) * 100 || 0
  const totalWastagePercentage = totalSalesAmount > 0 && percentage
  const { lowerWastageBounds, upperWastageBounds } = wastageBoundaries || {}

  const getWastageClass = (value: number) => {
    if (typeof lowerWastageBounds !== 'number' || typeof upperWastageBounds !== 'number') {
      return
    }
    if (value < lowerWastageBounds) return css.below
    if (value > upperWastageBounds) return css.above
    return css.within
  }

  const level3ProductGroupRow = (
    <TableRow className={cx(css.headingRow)} key={id}>
      <TableCell className={cx(css.tableCell, css.arrowCell)}>
        <a className={cx(css.arrow, { [css.open]: expanded })} onClick={toggleExpanded}>
          <KeyboardArrowRightIcon />
        </a>
      </TableCell>
      <TableCell className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`monthlyInfo.heading.productGroup${productGroupName}`)}
      </TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>{Math.round(totalSalesVolume || 0)} kpl</TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>{Math.round(totalWastageVolume || 0)} kpl</TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>{Math.round(totalWastageAmount || 0)} &euro;</TableCell>
      <TableCell className={cx(css.tableCell, css.numericCell)}>
        <span className={cx(getWastageClass(percentage), css.headingCell)}>
          {totalWastagePercentage ? totalWastagePercentage.toFixed(2) + ' %' : '0 %'}
        </span>
        {typeof lowerWastageBounds === 'number' && typeof upperWastageBounds === 'number' && (
          <span>{` (${lowerWastageBounds} - ${upperWastageBounds} %)`}</span>
        )}
      </TableCell>
      <TableCell className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            monthlyInfoId={level3ProductGroup.id}
            subject='Kitchen'
            isActionRequired={level3ProductGroup.isActionRequired}
            refetch={refetch}
          />
        )}
      </TableCell>
    </TableRow>
  )

  const rows = expanded
    ? [
        level3ProductGroupRow,
        ...level4ProductGroups.map((item) => {
          const itemPercentage = (item.totalWastageAmount / item.totalSalesAmount) * 100 || 0
          const itemTotalWastagePercentage = item.totalSalesAmount > 0 && itemPercentage
          return (
            <TableRow key={item.id}>
              <TableCell className={cx(css.tableCell, css.arrowCell)} />
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {i18n.t(`monthlyInfo.heading.productGroup${item.productGroupName}`)}
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {Math.round(item.totalSalesVolume || 0)} kpl
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {Math.round(item.totalWastageVolume || 0)} kpl
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                {Math.round(item.totalWastageAmount || 0)} &euro;
              </TableCell>
              <TableCell className={cx(css.tableCell, css.numericCell)}>
                <span className={getWastageClass(itemPercentage)}>
                  {itemTotalWastagePercentage ? itemTotalWastagePercentage.toFixed(2) + ' %' : '0 %'}
                </span>
              </TableCell>
              <TableCell className={cx(css.tableCell, css.actionCell)}>
                {canEdit && (
                  <RequiresAction
                    stationId={stationId}
                    date={date}
                    monthlyInfoId={item.id}
                    subject='Kitchen'
                    isActionRequired={item.isActionRequired}
                    showCreateTaskButton={false}
                    refetch={refetch}
                  />
                )}
              </TableCell>
            </TableRow>
          )
        }),
      ]
    : level3ProductGroupRow

  return rows
}

type ProductGroupWastageProps = {
  stationId: string
  date: string
}

const ProductGroupWastage = ({ stationId, date }: ProductGroupWastageProps) => (
  <Query<
    {
      monthlyInfo: Array<MonthlyInfoKitchen>
    },
    MonthlyInfoVariables
  >
    query={fetchMonthlyInfo}
    variables={{
      stationId,
      date,
      type: 'Kitchen',
    }}
  >
    {({ data, error, loading, refetch }) => {
      const productGroups = get(data, 'monthlyInfo', [])
      const groupedProductGroups = productGroups.reduce((res, item) => {
        const level3ProductGroupId = item.productGroupId.substring(0, 7)
        if (res[level3ProductGroupId]) {
          res[level3ProductGroupId] = [...res[level3ProductGroupId], item]
        } else {
          res[level3ProductGroupId] = [item]
        }
        return res
      }, {})
      const allWastageBoundaries = get(data, 'wastageBoundaries', [])
      return (
        <Loader
          {...{
            error,
            loading,
          }}
        >
          <div className={css.salesIncentive}>
            <Typography variant='h6' gutterBottom>
              {i18n.t('monthlyInfo.title.productGroupWastage', {
                month: moment(date).format('MMMM'),
              })}
            </Typography>
            <RestrictedConsumer>
              {(hasPermission) => (
                <Paper>
                  <Table size='small' className={css.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={cx(css.tableHeading, css.arrowCell)} />
                        <TableCell className={css.tableHeading}>
                          {i18n.t('monthlyInfo.heading.productGroupName')}
                        </TableCell>
                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('monthlyInfo.heading.actualProductsSold')}
                        </TableCell>
                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('monthlyInfo.heading.wastageAmount')}
                        </TableCell>
                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('monthlyInfo.heading.wastageEuros')}
                        </TableCell>
                        <TableCell className={cx(css.tableHeading, css.numericCell)}>
                          {i18n.t('monthlyInfo.heading.wastagePercentage')}
                        </TableCell>
                        <TableCell className={cx(css.tableHeading, css.actionCell)}>
                          {hasPermission && i18n.t('monthlyInfo.heading.isActionRequired')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.values(groupedProductGroups)
                        .filter((productGroups) =>
                          productGroups.some((productGroup) => productGroup.productGroupId.length === 7),
                        )
                        .map((productGroups) => {
                          const level3ProductGroup = productGroups.filter(
                            (productGroup) => productGroup.productGroupId.length === 7,
                          )[0]
                          const wastageBoundaries = allWastageBoundaries.find(
                            (boundary) => boundary.productType === level3ProductGroup.productGroupName.toLowerCase(),
                          )
                          return (
                            <ProductGroupWastageRow
                              stationId={stationId}
                              date={date}
                              data={productGroups}
                              wastageBoundaries={wastageBoundaries}
                              canEdit={hasPermission}
                              key={level3ProductGroup.id}
                              refetch={refetch}
                            />
                          )
                        })}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </RestrictedConsumer>
          </div>
        </Loader>
      )
    }}
  </Query>
)

const ScoreCardTable = ({ stationId, date, currentMonth }) => {
  const getColorClassName = ([category, values]: Row, type: string): string | null | undefined => {
    const { actual, high, low } = values[type]
    let className

    if (!actual && actual !== 0) {
      return null
    }

    className = css.colorCircleYellow

    if (actual >= high) {
      className = css.colorCircleGreen
    } else if (actual <= low) {
      className = css.colorCircleRed
    }

    return className
  }

  return (
    <Query
      query={fetchKitchenPageScoreCardStats}
      variables={{
        stationId,
        date: currentMonth,
      }}
    >
      {({ loading, error, data }) => {
        if (loading || error) {
          return <CircularProgress />
        }

        return (
          <Paper className={css.scoreCard}>
            <Table className={css.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderName')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderRollingYear')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderLastMonth')}</TableCell>
                  <TableCell className={css.tableHeading}>{i18n.t('scoreCard.columnHeaderThisMonth')}</TableCell>
                  <TableCell className={cx(css.tableHeading, css.actionCell)}>
                    {i18n.t('monthlyInfo.heading.isActionRequired')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(removeTypename(data.scoreCardStats)).map((row: any, i: number) => {
                  const [category, values] = row
                  const { rollingYear, lastMonth, thisMonth } = values || {}

                  return (
                    <TableRow key={i}>
                      <TableCell className={css.tableCell}>{i18n.t(`scoreCard.${category}`)}</TableCell>
                      <TableCell className={css.tableCell}>
                        <div className={css.cellValue}>
                          <span className={cx(css.colorCircle, getColorClassName(row, 'rollingYear'))} />
                          {rollingYear.actual}
                        </div>
                      </TableCell>
                      <TableCell className={css.tableCell}>
                        <div className={css.cellValue}>
                          <span className={cx(css.colorCircle, getColorClassName(row, 'lastMonth'))} />
                          {lastMonth.actual}
                        </div>
                      </TableCell>
                      <TableCell className={css.tableCell}>
                        {values.thisMonth && (
                          <div className={css.cellValue}>
                            <span className={cx(css.colorCircle, getColorClassName(row, 'thisMonth'))} />
                            {thisMonth.actual}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className={css.actionCell}>
                        <Query
                          query={fetchMonthlyInfoScoreCard}
                          variables={{
                            stationId,
                            date,
                            type: 'ScoreCard',
                            row: `kitchen_${category}`,
                          }}
                        >
                          {({ loading, error, data, refetch }) => {
                            if (loading || error) {
                              return <CircularProgress />
                            }

                            const info = get(data, 'monthlyInfo.0', {})
                            return (
                              <RequiresAction
                                stationId={stationId}
                                date={date}
                                subject='Kitchen'
                                isActionRequired={info.isActionRequired}
                                monthlyInfoId={info.id}
                                refetch={refetch}
                              />
                            )
                          }}
                        </Query>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Paper>
        )
      }}
    </Query>
  )
}

@withUser
class KitchenPage extends React.Component {
  constructor() {
    super()
    this.state = {
      imageOverlayIsOpen: false,
      selectedImage: null,
    }
  }

  selectImage = (imageContent: string) => {
    this.setState({
      selectedImage: imageContent,
      imageOverlayIsOpen: true,
    })
  }

  render() {
    const { user } = this.props
    const { imageOverlayIsOpen, selectedImage } = this.state
    return (
      <MonthlyInfoPage className={css.component} subject='Kitchen'>
        {({ stationId, date, dateFormat, onDateChange, companyId, currentMonth }) => (
          <React.Fragment>
            <ImageOverlay
              isOpen={imageOverlayIsOpen}
              image={selectedImage}
              onClose={() =>
                this.setState({
                  selectedImage: null,
                  imageOverlayIsOpen: false,
                })
              }
            />

            <div className={css.productGroupWastage}>
              <ProductGroupWastage stationId={stationId} date={date} />
            </div>

            <div className={css.scorecardTable}>
              <ScoreCardTable stationId={stationId} date={date} currentMonth={currentMonth} />
            </div>

            <Images
              stationId={stationId}
              selectImage={this.selectImage}
              user={user}
              imageType={'monthlyinfo_kitchen'}
            />

            <Links stationId={stationId} linkType={'monthlyinfo_kitchen'} companyId={companyId} />
          </React.Fragment>
        )}
      </MonthlyInfoPage>
    )
  }
}

export default KitchenPage
